import React, { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../store/reducers';
import {
    getChartType,
    getDentalExaminations,
    getProcessedDentalExaminations,
} from '../../../../store/selectors/prescriptionPad';
import PadV2PxElementCard from '../../rightPanel/workspace/padV2/PadV2PxElementCard';
import { DentalExaminationIcon } from '../../../common/Icons';
import Chart, { KeyToSetObject } from './Chart';
import {
    DentalExamination as DentalExaminationType,
    Surface,
} from '../../../../store/types/prescription';
import { notification } from 'antd';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../../store/actions';
import {
    deleteDentalExaminations,
    setChartType,
    setProcessedDentalExaminations,
    editDentalExaminationsGroup,
} from '../../../../store/actions/prescriptionPad';
import {
    ALL_TEETH_ID,
    ORAL_FINDINGS,
    Teeth,
    allSurfacesIdToName,
    lowerTeeth,
    teethToSurfacesIds,
    upperTeeth,
} from './constants';
import { track } from '../../../../tracking';
import { ChartType } from './DentalChart';
import DentalVisualisationsModal from './DentalVisualisationsModal';
import RenderHeaderRight from './DentalExamination/RenderHeaderRight';
import DentalExaminationComponent from './DentalExamination/DentalExaminationComponent';
import cloneDeep from 'lodash/cloneDeep';
import { focusCard } from '../../rightPanel/workspace/focusCard';
import { DENTAL_CHART_CONTAINER, DENTAL_CHART_CONTENT } from './Utils';

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    dentalExaminations?: DentalExaminationType[];
    chartType: ChartType;
    processedDentalExaminations: ProcessedDentalExaminations[];
}

interface State {
    isDentalVisualisationsModalVisible?: boolean;
    upperTeethToShow: Teeth[];
    lowerTeethToShow: Teeth[];
    selectedMultiTeeth: KeyToSetObject;
    isSelectingMultiTeeth?: boolean;
    editingMultiTeethId?: string;
}

export interface ProcessedDentalExaminations {
    teeth: string;
    surfaces: Set<string>;
    group?: Group;
}

export interface Group {
    id: string;
    data: KeyToSetObject;
}

export interface TeethToSurfacesObject {
    [k: string]: Surface[];
}

export const DENTAL_EXAMINATION_AS_INPUT = 'DENTAL_EXAMINATION_AS_INPUT';
export const DENTAL_EXAMINATION_AS_EL_ID = 'DENTAL_EXAMINATION_AS_EL_ID';

class DentalExamination extends PureComponent<Props, State> {
    inputRef: React.RefObject<HTMLInputElement> = React.createRef();
    DentalExaminationIcon = DentalExaminationIcon();

    state: State = {
        upperTeethToShow: upperTeeth(),
        lowerTeethToShow: lowerTeeth(),
        selectedMultiTeeth: {},
    };

    static getDerivedStateFromProps(props: Props) {
        const { chartType } = props;
        const upperTeethToShow = upperTeeth(chartType);
        const lowerTeethToShow = lowerTeeth(chartType);
        return {
            upperTeethToShow,
            lowerTeethToShow,
        };
    }

    processedDentalExaminations = (dentalExaminations?: DentalExaminationType[]) => {
        if (!dentalExaminations || !dentalExaminations.length) return;
        const { processedDentalExaminations, dispatch } = this.props;
        processedDentalExaminations.forEach((i) => (i.surfaces = new Set()));
        for (let i = 0; i < dentalExaminations.length; i++) {
            const examination = dentalExaminations[i];
            if (!examination.teeth_id) continue;
            if (examination.group_id) {
                const currentTeeth = processedDentalExaminations.find(
                    (i) => i.group?.id === examination.group_id,
                );
                if (currentTeeth) {
                    examination.surfaces?.forEach((i) => {
                        currentTeeth.group?.data[examination.teeth_id || ''].add(i.id);
                    });
                } else {
                    processedDentalExaminations.push({
                        teeth: examination.teeth_id,
                        surfaces: new Set(examination.surfaces?.map((i) => i.id)),
                    });
                }
            } else {
                const currentTeeth = processedDentalExaminations.find(
                    (i) => i.teeth === examination.teeth_id,
                );
                if (currentTeeth) {
                    examination.surfaces?.forEach((i) => {
                        currentTeeth.surfaces.add(i.id);
                    });
                } else {
                    processedDentalExaminations.push({
                        teeth: examination.teeth_id,
                        surfaces: new Set(examination.surfaces?.map((i) => i.id)),
                    });
                }
            }
        }
        dispatch(setProcessedDentalExaminations(Array.from(processedDentalExaminations)));
    };

    selectTeeth = (teeth: string, surfaces?: Set<string>, group?: Group) => {
        const { processedDentalExaminations, dispatch } = this.props;
        const { editingMultiTeethId } = this.state;
        const { clearMultiTeeth } = this;
        const currentTeeth = processedDentalExaminations?.find((i) => i.teeth === teeth);
        if (editingMultiTeethId) {
            dispatch(editDentalExaminationsGroup(editingMultiTeethId, teeth));
            clearMultiTeeth();
        } else {
            if (currentTeeth) {
                notification.success({
                    message: `${group ? 'Teeth are' : 'Tooth is'} already selected!`,
                    duration: 5,
                });
                const surfacesArray = Array.from(surfaces && surfaces.size > 0 ? surfaces : []);
                surfacesArray.forEach((surface) => {
                    currentTeeth.surfaces.add(surface);
                });
                focusCard(`teeth-${teeth}`);
            } else {
                processedDentalExaminations.push({
                    teeth: teeth,
                    surfaces: surfaces && surfaces.size > 0 ? surfaces : new Set([]),
                    group,
                });
                dispatch(setProcessedDentalExaminations(Array.from(processedDentalExaminations)));
            }
        }
        track('DENTAL_CHART_TEETH_SELECT', {
            teeth: teeth,
            editingMultiTeethId,
        });
    };

    selectSurface = (teeth: string, s: number) => {
        const { chartType } = this.props;
        const surface = teethToSurfacesIds(chartType)[teeth][s];
        const { processedDentalExaminations, dispatch } = this.props;
        const currentTeeth = processedDentalExaminations?.find((i) => i.teeth === teeth);
        if (currentTeeth) {
            if (currentTeeth?.surfaces) {
                const currentSurfaces = currentTeeth.surfaces;
                const currentSurface = currentSurfaces.has(surface);
                if (currentSurface) currentSurfaces.delete(surface);
                else currentSurfaces.add(surface);
                currentTeeth.surfaces = currentSurfaces;
            } else {
                currentTeeth.surfaces = new Set([surface]);
            }
        } else {
            processedDentalExaminations.push({
                teeth: teeth,
                surfaces: new Set([surface]),
            });
        }
        dispatch(setProcessedDentalExaminations(Array.from(processedDentalExaminations)));
        track('DENTAL_CHART_SURFACE_SELECT', {
            teeth: teeth,
            surface: surface,
        });
    };

    onTeethDelete = (teeth: string, group?: Group) => {
        const { dispatch } = this.props;
        let { processedDentalExaminations } = this.props;
        processedDentalExaminations = processedDentalExaminations.filter((i) => i.teeth !== teeth);
        dispatch(setProcessedDentalExaminations(Array.from(processedDentalExaminations)));
        dispatch(deleteDentalExaminations(teeth, group));
    };

    onUpdate = () => {
        const { dentalExaminations } = this.props;
        const { processedDentalExaminations } = this;
        processedDentalExaminations(dentalExaminations);
    };

    onAddOralFindings = () => {
        const { processedDentalExaminations, dispatch } = this.props;
        const currentTeeth = processedDentalExaminations?.find((i) => i.teeth === ALL_TEETH_ID);
        if (currentTeeth) {
            notification.success({
                message: 'Oral Findings is already selected!',
                duration: 5,
            });
        } else {
            processedDentalExaminations.push({
                teeth: ALL_TEETH_ID,
                surfaces: new Set([]),
            });
            dispatch(setProcessedDentalExaminations(Array.from(processedDentalExaminations)));
        }
        track('DENTAL_CHART_ALL_TEETH_SELECT');
    };

    toggleIsDentalVisualisationsModalVisible = () => {
        const { isDentalVisualisationsModalVisible } = this.state;
        this.setState({
            isDentalVisualisationsModalVisible: !isDentalVisualisationsModalVisible,
        });
        track('DENTAL_VISUALISATIONS_MODAL_TOGGLE', {
            action: isDentalVisualisationsModalVisible ? 'close' : 'open',
        });
    };

    onChartTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { dispatch } = this.props;
        const chartType = e.target.value as ChartType;
        dispatch(setChartType(chartType));
    };

    selectMultiTeeth = (teeth: string, s?: number) => {
        const { chartType } = this.props;
        const { selectedMultiTeeth } = this.state;
        const surface = s ? teethToSurfacesIds(chartType)[teeth][s] : '';
        if (surface) {
            if (selectedMultiTeeth[teeth]) {
                if (selectedMultiTeeth[teeth].has(surface)) {
                    selectedMultiTeeth[teeth].delete(surface);
                } else {
                    selectedMultiTeeth[teeth].add(surface);
                }
            } else {
                selectedMultiTeeth[teeth] = new Set([surface]);
            }
        } else if (selectedMultiTeeth[teeth]) {
            delete selectedMultiTeeth[teeth];
        } else {
            selectedMultiTeeth[teeth] = new Set();
        }
        this.setState({
            selectedMultiTeeth: cloneDeep(selectedMultiTeeth),
        });
    };

    addMultiTeeth = () => {
        const { selectedMultiTeeth } = this.state;
        const { selectTeeth, clearMultiTeeth } = this;
        const teethArray = Object.keys(selectedMultiTeeth).sort();
        if (teethArray.length === 0) {
            notification.error({
                message: 'Please select at least one tooth!',
                duration: 5,
            });
            return;
        }
        if (teethArray.length === 1) {
            const teeth = teethArray[0];
            selectTeeth(teeth, selectedMultiTeeth[teeth]);
            clearMultiTeeth();
            return;
        }
        let teeth = '';
        const surfaceSet: Set<string> = new Set();
        teethArray.forEach((i, index) => {
            teeth += i;
            const surfaces = Array.from(selectedMultiTeeth[i]).filter(Boolean).sort();
            teeth += surfaces.length > 0 ? `@${surfaces.join(',')}` : '';
            if (teethArray.length - 1 > index) teeth += '|';
            surfaces.forEach((surface) => surfaceSet.add(surface));
        });
        const group = {
            id: teeth,
            data: selectedMultiTeeth,
        };
        selectTeeth(teeth, surfaceSet, group);
        clearMultiTeeth();
        track('DENTAL_CHART_MULTI_TEETH_SELECT', {
            teeth: teeth,
        });
    };

    clearMultiTeeth = () => {
        this.setState({
            selectedMultiTeeth: {},
            editingMultiTeethId: undefined,
            isSelectingMultiTeeth: false,
        });
    };

    onTeethGroupEdit = (group: Group) => {
        const selectedMultiTeeth: KeyToSetObject = {};
        for (const key in group.data) {
            selectedMultiTeeth[key] = group.data[key];
        }
        this.setState({ selectedMultiTeeth, editingMultiTeethId: group.id });
        focusCard(DENTAL_CHART_CONTAINER);
    };

    setIsSelectingMultiTeeth = () => {
        this.setState({ isSelectingMultiTeeth: true });
    };

    render(): ReactNode {
        const { dentalExaminations, processedDentalExaminations } = this.props;
        const {
            isDentalVisualisationsModalVisible,
            upperTeethToShow,
            lowerTeethToShow,
            selectedMultiTeeth,
            isSelectingMultiTeeth,
        } = this.state;
        const {
            DentalExaminationIcon,
            selectTeeth,
            selectSurface,
            onTeethDelete,
            onUpdate,
            onAddOralFindings,
            toggleIsDentalVisualisationsModalVisible,
            onChartTypeChange,
            selectMultiTeeth,
            addMultiTeeth,
            clearMultiTeeth,
            onTeethGroupEdit,
            setIsSelectingMultiTeeth,
        } = this;
        const selectedMultiTeethArrayLength = Object.keys(selectedMultiTeeth).length;
        const isSelectedMultiTeethArray =
            isSelectingMultiTeeth || Object.keys(selectedMultiTeeth).length > 0;

        return (
            <PadV2PxElementCard
                icon={DentalExaminationIcon}
                headingText={'Dental Examinations / Past Procedures'}
                iconContainerBgClassName="bg-darwin-green-primary"
                bodyCssClassName="rounded-t-8"
                style={{ paddingBottom: '0' }}
                renderHeaderRight={() => (
                    <RenderHeaderRight
                        onChartTypeChange={onChartTypeChange}
                        toggleIsDentalVisualisationsModalVisible={
                            toggleIsDentalVisualisationsModalVisible
                        }
                    />
                )}
                headingTextCssClassName="truncate-overflow-1"
            >
                {processedDentalExaminations.map(({ teeth, surfaces, group }) => {
                    let elements;
                    if (group) {
                        elements = dentalExaminations?.filter(
                            (i) =>
                                i.group_id === group.id &&
                                i.teeth_id === Object.keys(group.data)[0],
                        );
                    } else {
                        elements = dentalExaminations?.filter(
                            (i) => i.teeth_id === teeth && !i.group_id,
                        );
                    }
                    elements?.forEach((i) => {
                        i.surfaces?.forEach((i) => {
                            surfaces.add(i.id);
                        });
                    });
                    const surfacesArray = Array.from(surfaces).map((id) => ({
                        id,
                        name: allSurfacesIdToName[id],
                    }));
                    return (
                        <DentalExaminationComponent
                            teeth={teeth}
                            surfaces={surfaces}
                            onTeethDelete={onTeethDelete}
                            elements={elements}
                            surfacesArray={surfacesArray}
                            onUpdate={onUpdate}
                            group={group}
                            onTeethGroupEdit={() => group && onTeethGroupEdit(group)}
                        />
                    );
                })}
                <div id={DENTAL_CHART_CONTAINER}>
                    {selectedMultiTeethArrayLength || isSelectedMultiTeethArray ? (
                        <div className="mt-12 px-12 py-4 rounded-4 bg-darwin-accent-bright-blue-800 flex items-center justify-between">
                            <div className="flex items-center space-x-8">
                                <p className="pt-1">🦷</p>
                                <p className="text-13 font-700 text-white">
                                    {selectedMultiTeethArrayLength} selected
                                </p>
                            </div>
                            <div className="flex items-center space-x-8">
                                <button
                                    className="px-8 py-1 rounded-4 text-darwin-accent-bright-blue-800 text-13 font-700 flex items-center space-x-8 bg-white"
                                    onClick={addMultiTeeth}
                                >
                                    <p>
                                        <svg
                                            className="w-12 h-8 fill-darwin-accent-bright-blue-800"
                                            viewBox="0 0 12 9"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M11.5098 0.240234C11.7383 0.494141 11.7383 0.875 11.5098 1.10352L4.80664 7.80664C4.55273 8.06055 4.17188 8.06055 3.94336 7.80664L0.490234 4.35352C0.236328 4.125 0.236328 3.74414 0.490234 3.51562C0.71875 3.26172 1.09961 3.26172 1.32812 3.51562L4.34961 6.53711L10.6465 0.240234C10.875 0.0117188 11.2559 0.0117188 11.4844 0.240234H11.5098Z" />
                                        </svg>
                                    </p>
                                    <p>Done</p>
                                </button>
                                <button
                                    className="px-8 py-4 rounded-4 text-white text-13 font-700"
                                    onClick={clearMultiTeeth}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between pt-12 pb-8">
                            <div className="flex items-center space-x-8">
                                <p>
                                    <svg
                                        className="w-14 h-14 fill-darwin-neutrals-600"
                                        viewBox="0 0 14 14"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M7 1.84375C5.0957 1.84375 3.36914 2.85938 2.4043 4.48438C1.46484 6.13477 1.46484 8.14062 2.4043 9.76562C3.36914 11.416 5.0957 12.4062 7 12.4062C8.87891 12.4062 10.6055 11.416 11.5703 9.76562C12.5098 8.14062 12.5098 6.13477 11.5703 4.48438C10.6055 2.85938 8.87891 1.84375 7 1.84375ZM7 13.625C4.66406 13.625 2.53125 12.4062 1.36328 10.375C0.195312 8.36914 0.195312 5.90625 1.36328 3.875C2.53125 1.86914 4.66406 0.625 7 0.625C9.31055 0.625 11.4434 1.86914 12.6113 3.875C13.7793 5.90625 13.7793 8.36914 12.6113 10.375C11.4434 12.4062 9.31055 13.625 7 13.625ZM5.98438 9.15625H6.59375V7.53125H5.98438C5.62891 7.53125 5.375 7.27734 5.375 6.92188C5.375 6.5918 5.62891 6.3125 5.98438 6.3125H7.20312C7.5332 6.3125 7.8125 6.5918 7.8125 6.92188V9.15625H8.01562C8.3457 9.15625 8.625 9.43555 8.625 9.76562C8.625 10.1211 8.3457 10.375 8.01562 10.375H5.98438C5.62891 10.375 5.375 10.1211 5.375 9.76562C5.375 9.43555 5.62891 9.15625 5.98438 9.15625ZM7 5.5C6.54297 5.5 6.1875 5.14453 6.1875 4.6875C6.1875 4.25586 6.54297 3.875 7 3.875C7.43164 3.875 7.8125 4.25586 7.8125 4.6875C7.8125 5.14453 7.43164 5.5 7 5.5Z" />
                                    </svg>
                                </p>
                                <p className="text-13 text-darwin-neutral_new-800">
                                    Select teeth from the chart below to add dental examinations or
                                    past procedures
                                </p>
                            </div>
                            <div className="flex items-center space-x-12">
                                <button
                                    className="flex items-center space-x-5 border border-darwin-accent-bright-blue-800 rounded-4 px-8 py-4 bg-white"
                                    onClick={onAddOralFindings}
                                >
                                    <p>
                                        <svg
                                            className="w-20 h-14 fill-darwin-accent-bright-blue-800"
                                            viewBox="0 0 19 15"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M0.5 3.27271C0.5 1.61646 1.84375 0.272705 3.5 0.272705H15.5C17.1562 0.272705 18.5 1.61646 18.5 3.27271V11.2727C18.5 12.929 17.1562 14.2727 15.5 14.2727H3.5C1.84375 14.2727 0.5 12.929 0.5 11.2727V3.27271ZM6 4.77271V6.52271C6 6.96021 6.3125 7.27271 6.75 7.27271H8.25C8.65625 7.27271 9 6.96021 9 6.52271V4.77271C9 3.96021 8.3125 3.27271 7.5 3.27271C6.65625 3.27271 6 3.96021 6 4.77271ZM11.5 3.27271C10.6562 3.27271 10 3.96021 10 4.77271V6.52271C10 6.96021 10.3125 7.27271 10.75 7.27271H12.25C12.6562 7.27271 13 6.96021 13 6.52271V4.77271C13 3.96021 12.3125 3.27271 11.5 3.27271ZM2 5.77271V6.52271C2 6.96021 2.3125 7.27271 2.75 7.27271H4.25C4.65625 7.27271 5 6.96021 5 6.52271V5.77271C5 4.96021 4.3125 4.27271 3.5 4.27271C2.65625 4.27271 2 4.96021 2 5.77271ZM3.5 11.2727C4.3125 11.2727 5 10.6165 5 9.77271V9.02271C5 8.61646 4.65625 8.27271 4.25 8.27271H2.75C2.3125 8.27271 2 8.61646 2 9.02271V9.77271C2 10.6165 2.65625 11.2727 3.5 11.2727ZM6 9.77271C6 10.6165 6.65625 11.2727 7.5 11.2727C8.3125 11.2727 9 10.6165 9 9.77271V9.02271C9 8.61646 8.65625 8.27271 8.25 8.27271H6.75C6.3125 8.27271 6 8.61646 6 9.02271V9.77271ZM11.5 11.2727C12.3125 11.2727 13 10.6165 13 9.77271V9.02271C13 8.61646 12.6562 8.27271 12.25 8.27271H10.75C10.3125 8.27271 10 8.61646 10 9.02271V9.77271C10 10.6165 10.6562 11.2727 11.5 11.2727ZM14 5.77271V6.52271C14 6.96021 14.3125 7.27271 14.75 7.27271H16.25C16.6562 7.27271 17 6.96021 17 6.52271V5.77271C17 4.96021 16.3125 4.27271 15.5 4.27271C14.6562 4.27271 14 4.96021 14 5.77271ZM15.5 11.2727C16.3125 11.2727 17 10.6165 17 9.77271V9.02271C17 8.61646 16.6562 8.27271 16.25 8.27271H14.75C14.3125 8.27271 14 8.61646 14 9.02271V9.77271C14 10.6165 14.6562 11.2727 15.5 11.2727Z" />
                                        </svg>
                                    </p>
                                    <p className="text-darwin-accent-bright-blue-800 text-12 font-700">
                                        {ORAL_FINDINGS}
                                    </p>
                                </button>
                                <button
                                    className="flex items-center space-x-5 border border-darwin-accent-bright-blue-800 rounded-4 px-8 py-4 bg-white"
                                    onClick={setIsSelectingMultiTeeth}
                                >
                                    <p>
                                        <svg
                                            className="w-12 h-12 fill-darwin-accent-bright-blue-800"
                                            viewBox="0 0 15 15"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M2.06665 0.272705H12.0667C13.1604 0.272705 14.0667 1.17896 14.0667 2.27271V12.2727C14.0667 13.3977 13.1604 14.2727 12.0667 14.2727H2.06665C0.94165 14.2727 0.0666504 13.3977 0.0666504 12.2727V2.27271C0.0666504 1.17896 0.94165 0.272705 2.06665 0.272705ZM10.5979 5.80396H10.5667C10.8792 5.52271 10.8792 5.05396 10.5667 4.74146C10.2854 4.46021 9.81665 4.46021 9.5354 4.74146L6.06665 8.24146L4.5979 6.77271C4.2854 6.46021 3.81665 6.46021 3.5354 6.77271C3.2229 7.05396 3.2229 7.52271 3.5354 7.80396L5.5354 9.80396C5.81665 10.1165 6.2854 10.1165 6.5979 9.80396L10.5979 5.80396Z" />
                                        </svg>
                                    </p>
                                    <p className="text-darwin-accent-bright-blue-800 text-12 font-700">
                                        Select Multiple
                                    </p>
                                </button>
                            </div>
                        </div>
                    )}
                    <div id={DENTAL_CHART_CONTENT}>
                        <Chart
                            selectTeeth={selectTeeth}
                            selectSurface={selectSurface}
                            upperTeethToShow={upperTeethToShow}
                            lowerTeethToShow={lowerTeethToShow}
                            selectedMultiTeeth={selectedMultiTeeth}
                            selectMultiTeeth={selectMultiTeeth}
                            isSelectedMultiTeethArray={isSelectedMultiTeethArray}
                        />
                    </div>
                </div>
                <DentalVisualisationsModal
                    visible={isDentalVisualisationsModalVisible}
                    closeMe={toggleIsDentalVisualisationsModalVisible}
                />
            </PadV2PxElementCard>
        );
    }
}

export default connect((state: GlobalState) => ({
    dentalExaminations: getDentalExaminations(state),
    chartType: getChartType(state),
    processedDentalExaminations: getProcessedDentalExaminations(state),
}))(DentalExamination);
